import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  addNativeGoogleListener,
  getNativeGoogleNavigationPlugin,
  removeNativeGoogleListener,
  setNativeGoogleMuted,
  startNativeGoogleNavigation,
  stopNativeGoogleNavigation,
  updateNativeGoogleNavigation,
  setNativeScreenAwake,
} from "@/native/googleNavigationBridge";

const API = process.env.REACT_APP_BACKEND_URL || "";

export const EMPTY_NAVIGATION_ITEMS = Object.freeze([]);
const NATIVE_TOMTOM_OPERATION_QUEUE = Symbol.for("ndg.google.operationQueue");
const NATIVE_TOMTOM_STOP_FAILURE = Symbol.for("ndg.google.stopFailure");

function queueNativeGoogleOperation(plugin, operation) {
  if (!plugin) return Promise.resolve();
  const previous = plugin[NATIVE_TOMTOM_OPERATION_QUEUE] || Promise.resolve();
  const run = previous.then(() => operation(plugin[NATIVE_TOMTOM_STOP_FAILURE]));
  plugin[NATIVE_TOMTOM_OPERATION_QUEUE] = run.catch(() => {});
  return run;
}

export function parseRouteGeometry(raw) {
  let points = raw;
  if (typeof raw === "string" && raw.trim()) {
    try {
      points = JSON.parse(raw);
    } catch {
      // Google Routes returns encodedPolyline (precision 1e-5). Decode its
      // road vertices instead of treating a valid response as an empty map.
      points = [];
      let index = 0, latitude = 0, longitude = 0;
      while (index < raw.length) {
        const deltas = [];
        for (let coordinate = 0; coordinate < 2; coordinate++) {
          let result = 0, shift = 0, byte;
          do {
            if (index >= raw.length || shift > 30) return [];
            byte = raw.charCodeAt(index++) - 63;
            if (byte < 0 || byte > 63) return [];
            result += (byte & 31) * (2 ** shift);
            shift += 5;
          } while (byte >= 32);
          deltas.push(result % 2 ? -Math.floor(result / 2) - 1 : result / 2);
        }
        latitude += deltas[0]; longitude += deltas[1];
        points.push([latitude / 100000, longitude / 100000]);
      }
    }
  }
  return Array.isArray(points) && points.every(point => Array.isArray(point)
    && point.length >= 2 && typeof point[0] === "number" && typeof point[1] === "number"
    && Number.isFinite(point[0]) && Number.isFinite(point[1])
    && Math.abs(point[0]) <= 90 && Math.abs(point[1]) <= 180) ? points : [];
}

export function hasPreparedLessonItinerary(waypoints) {
  // Saved lesson recipes omit transient road geometry, but retain the agreed
  // itinerary and its return. This hint only starts GPS; the authenticated
  // navigation-route endpoint still owns route selection and the deadline.
  return Array.isArray(waypoints) && waypoints.length > 0
    && waypoints[waypoints.length - 1]?.kind === "return"
    && waypoints.every((point) => (
      typeof point?.latitude === "number" && Number.isFinite(point.latitude)
      && typeof point?.longitude === "number" && Number.isFinite(point.longitude)
      && Math.abs(point.latitude) <= 90 && Math.abs(point.longitude) <= 180
    ));
}

export function distanceMeters(a, b) {
  if (!a || !b) return Number.POSITIVE_INFINITY;
  const lat1 = Number(a.latitude ?? a[0]);
  const lng1 = Number(a.longitude ?? a[1]);
  const lat2 = Number(b.latitude ?? b[0]);
  const lng2 = Number(b.longitude ?? b[1]);
  if (![lat1, lng1, lat2, lng2].every(Number.isFinite)) {
    return Number.POSITIVE_INFINITY;
  }
  const radians = (value) => value * Math.PI / 180;
  const dLat = radians(lat2 - lat1);
  const dLng = radians(lng2 - lng1);
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos(radians(lat1)) * Math.cos(radians(lat2)) * Math.sin(dLng / 2) ** 2;
  return 6371000 * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function freshPart3NavigationFix(fix, now = Date.now()) {
  return Number.isFinite(fix?.requestedAt) && now - fix.requestedAt <= 15000
    && fix.requestedAt <= now + 5000
    && Number.isFinite(fix?.accuracy) && fix.accuracy >= 0 && fix.accuracy <= 100
    && Number.isFinite(fix?.latitude) && Math.abs(fix.latitude) <= 90
    && Number.isFinite(fix?.longitude) && Math.abs(fix.longitude) <= 180;
}

export function part3RecentFixesAtFix(previous, fix, now = Date.now()) {
  const recent = (Array.isArray(previous) ? previous : [])
    .filter(row => now - row.requestedAt <= 60000 && row.requestedAt <= now + 5000);
  if (!freshPart3NavigationFix(fix, now)
      || recent.length && fix.requestedAt - recent[recent.length - 1].requestedAt < 1000) return recent;
  // Device evidence is transient. The server alone decides checkpoint progress.
  return [...recent, { latitude: fix.latitude, longitude: fix.longitude,
    accuracy: fix.accuracy, requestedAt: fix.requestedAt,
    ...(Number.isFinite(fix.speed) && fix.speed >= 0 ? { speed: fix.speed } : {}) }].slice(-60);
}

export function part3InstructionProgressAtFix(fix, instructions, previous = null, now = Date.now()) {
  const state = previous || { index: 0, lastFixAt: null, closestDistance: null, closestAccuracy: null };
  if (!freshPart3NavigationFix(fix, now) || state.lastFixAt != null && fix.requestedAt <= state.lastFixAt
      || !Array.isArray(instructions) || instructions.length === 0) return state;
  let index = Math.max(0, Math.min(state.index, instructions.length - 1));
  let closestDistance = state.closestDistance, closestAccuracy = state.closestAccuracy;
  // Google includes a DEPART step at the current origin. It is the sole
  // instruction that proximity can skip; a nearby turn still needs guidance.
  if (index === 0 && instructions.length > 1 && instructions[0]?.maneuver === "DEPART"
      && distanceMeters(fix, instructions[0]) + fix.accuracy <= 30) {
    index = 1; closestDistance = null; closestAccuracy = null;
  }
  let distance = distanceMeters(fix, instructions[index]);
  if (index < instructions.length - 1 && closestDistance != null
      && distance - closestDistance >= Math.max(15, fix.accuracy + closestAccuracy)) {
    // A distinct later fix must show departure after a close approach.
    // Advance at most one manoeuvre per observation, even on a small circle.
    index += 1; closestDistance = null; closestAccuracy = null;
    distance = distanceMeters(fix, instructions[index]);
  }
  if (distance + fix.accuracy <= 30 && (closestDistance == null || distance < closestDistance)) {
    closestDistance = distance; closestAccuracy = fix.accuracy;
  }
  return { index, lastFixAt: fix.requestedAt, closestDistance, closestAccuracy };
}

export function part3PendingStopAtFix(fix, navigationWaypoints, now = Date.now()) {
  // This only requests server observation. It never marks a stop passed or
  // infers progress from time, a later stop, or proximity to the return point.
  const stop = Array.isArray(navigationWaypoints) ? navigationWaypoints[0] : null;
  const radius = stop?.reach_radius_metres ?? 75;
  if (stop?.kind !== "challenge" || !Number.isFinite(fix?.requestedAt)
      || now - fix.requestedAt > 15000 || fix.requestedAt > now + 5000
      || !Number.isFinite(fix?.accuracy) || fix.accuracy < 0 || fix.accuracy > 100
      || ![fix.latitude, fix.longitude, stop.latitude, stop.longitude].every(Number.isFinite)
      || Math.abs(fix.latitude) > 90 || Math.abs(stop.latitude) > 90
      || Math.abs(fix.longitude) > 180 || Math.abs(stop.longitude) > 180
      || !Number.isFinite(radius) || radius < 15 || radius > 75
      || distanceMeters(fix, stop) + fix.accuracy > radius) return null;
  return JSON.stringify([stop.id ?? null, stop.latitude, stop.longitude]);
}

function pointToSegmentMeters(point, start, end) {
  const lat0 = Number(point.latitude);
  const lng0 = Number(point.longitude);
  const scaleX = 111320 * Math.cos(lat0 * Math.PI / 180);
  const scaleY = 110540;
  const px = 0;
  const py = 0;
  const ax = (Number(start[1]) - lng0) * scaleX;
  const ay = (Number(start[0]) - lat0) * scaleY;
  const bx = (Number(end[1]) - lng0) * scaleX;
  const by = (Number(end[0]) - lat0) * scaleY;
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  const t = lengthSq
    ? Math.max(0, Math.min(1, (-(ax * dx + ay * dy)) / lengthSq))
    : 0;
  return Math.hypot(ax + t * dx, ay + t * dy);
}

export function nearestRouteDistanceMeters(point, geometry) {
  const route = parseRouteGeometry(geometry);
  if (!point || route.length < 2) return Number.POSITIVE_INFINITY;
  let nearest = Number.POSITIVE_INFINITY;
  for (let index = 1; index < route.length; index += 1) {
    nearest = Math.min(nearest, pointToSegmentMeters(point, route[index - 1], route[index]));
  }
  return nearest;
}

export function formatNavigationDistance(metres) {
  if (!Number.isFinite(metres)) return "";
  if (metres < 950) return `${Math.max(10, Math.round(metres / 10) * 10)} m`;
  return `${(metres / 1000).toFixed(metres < 10000 ? 1 : 0)} km`;
}

export function completedChallengesAtFix(
  fix, teachingChallenges = [], nextChallengePosition = 1,
) {
  const ordered = [...teachingChallenges]
    .filter((item) => Number.isFinite(Number(item?.position)))
    .sort((a, b) => Number(a.position) - Number(b.position));
  const completed = [];
  let nextPosition = Number(nextChallengePosition) || 1;
  for (const challenge of ordered) {
    const position = Number(challenge.position);
    if (position < nextPosition) continue;
    if (distanceMeters(fix, challenge) > 65) break;
    completed.push(challenge);
    nextPosition = position + 1;
  }
  return { completed, nextChallengePosition: nextPosition };
}

export function teachingChallengeForStop(stop, teachingChallenges = []) {
  if (!stop) return null;
  const stopKind = String(stop.kind || "").toLowerCase();
  if (["return", "test_centre", "search", "navigation", "waypoint"].includes(stopKind)) {
    return null;
  }
  const stopId = stop.environment_id ?? stop.id;
  // Stable identity connects normal route stops to learning evidence. Legacy
  // anonymous challenge waypoints remain compatible only with an anonymous
  // challenge at the same inferred position and location.
  return teachingChallenges.find((challenge) => {
    if (challenge === stop) return true;
    const challengeId = challenge.environment_id ?? challenge.id;
    if (stopId != null || challengeId != null) {
      return stopId != null
        && challengeId != null
        && String(stopId) === String(challengeId)
        && Number(stop.position) === Number(challenge.position);
    }
    return Number.isFinite(Number(stop.position))
      && Number(stop.position) === Number(challenge.position)
      && distanceMeters(stop, challenge) <= 65;
  }) || null;
}

export function navigationProgressAtFix(
  fix, navigationWaypoints = [], teachingChallenges = [], nextChallengePosition = 1,
) {
  // A centre/search stop is navigation, not evidence of a teaching challenge.
  const sequence = navigationWaypoints.length > 0
    ? navigationWaypoints
      .filter((item) => item.kind !== "return")
      .map((item, index) => ({
        ...item,
        position: Number.isFinite(Number(item.position)) ? Number(item.position) : index + 1,
      }))
    : teachingChallenges;
  const progress = completedChallengesAtFix(fix, sequence, nextChallengePosition);
  return { ...progress, completed: progress.completed
    .map((stop) => teachingChallengeForStop(stop, teachingChallenges)).filter(Boolean) };
}

export function buildNavigationRoutePayload(
  fix, nextChallengePosition, repeatChallengePosition = null, destination = null, returnOnly = false,
) {
  return {
    device_latitude: Number(fix.latitude),
    device_longitude: Number(fix.longitude),
    next_challenge_position: Number(nextChallengePosition),
    ...(repeatChallengePosition != null && Number.isFinite(Number(repeatChallengePosition))
      ? { repeat_challenge_position: Number(repeatChallengePosition) }
      : {}),
    ...(destination && typeof destination === "object" ? { destination } : {}),
    ...(returnOnly ? { return_only: true } : {}),
  };
}

function guardNativeGoogleCommands(plugin, shouldRun, commandNames) {
  if (!plugin) return plugin;
  const commandSet = new Set(commandNames);
  return new Proxy(plugin, {
    get(target, prop, receiver) {
      const value = Reflect.get(target, prop, target);
      if (typeof prop === "string" && commandSet.has(prop) && typeof value === "function") {
        return async (...args) => {
          if (!shouldRun()) return;
          return value.apply(target, args);
        };
      }
      if (typeof value === "function") return value.bind(target);
      return value;
    },
  });
}

export default function useLiveGoogleNavigation({
  lessonEventId,
  routeMode = "lesson",
  part3SessionId = null,
  part3Phase = null,
  part3PhaseVersion = null,
  onPart3Route = null,
  initialRouteGeometry,
  initialInstructions = EMPTY_NAVIGATION_ITEMS,
  initialWaypoints = EMPTY_NAVIGATION_ITEMS,
  teachingChallenges = EMPTY_NAVIGATION_ITEMS,
  onPosition,
  onChallengePassed,
  onChallengeRepeated,
  autoStart = false,
}) {
  const [active, setActive] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [muted, setMuted] = useState(false);
  const [speechStatus, setSpeechStatus] = useState("tap");
  const [device, setDevice] = useState({
    status: "idle", latitude: null, longitude: null, accuracy: null,
  });
  const [routeGeometry, setRouteGeometry] = useState(initialRouteGeometry);
  const [instructions, setInstructions] = useState(
    Array.isArray(initialInstructions) ? initialInstructions : EMPTY_NAVIGATION_ITEMS,
  );
  const [instructionIndex, setInstructionIndex] = useState(0);
  const [distanceToInstruction, setDistanceToInstruction] = useState(null);
  const [status, setStatus] = useState("Ready");
  const [error, setError] = useState(null);
  const [routeNotice, setRouteNotice] = useState(null);
  const [destinationPoint, setDestinationPoint] = useState(null);
  const [rerouting, setRerouting] = useState(false);
  const [lastCompletedChallenge, setLastCompletedChallenge] = useState(null);
  const [nativeMode, setNativeMode] = useState(false);
  const [nativeGuidance, setNativeGuidance] = useState(null);
  const [screenAwake, setScreenAwake] = useState("unknown");
  const [waypoints, setWaypoints] = useState(
    Array.isArray(initialWaypoints) ? initialWaypoints : EMPTY_NAVIGATION_ITEMS,
  );
  const normalizedRouteMode = String(routeMode || "lesson").toLowerCase();
  const isPart3Route = normalizedRouteMode === "part3";
  const part3ScopeKey = JSON.stringify([part3SessionId, part3Phase, part3PhaseVersion]);
  const part3ScopeRef = useRef(part3ScopeKey);
  part3ScopeRef.current = part3ScopeKey;
  const onPart3RouteRef = useRef(onPart3Route);
  onPart3RouteRef.current = onPart3Route;
  const part3RevisionRef = useRef(-1);
  const part3StopProbeRef = useRef(null);
  const part3AtDestinationRef = useRef(false);
  const part3LastFixAtRef = useRef(null);
  const part3RecentFixesRef = useRef([]);
  const part3InstructionProgressRef = useRef(null);
  const isHomeRoute = normalizedRouteMode === "home";
  const isPickupRoute = normalizedRouteMode === "pickup";
  const routeTargetId = lessonEventId;
  // Value identity survives ordinary parent renders, including reconstructed
  // saved waypoints, but revokes a diversion when Adapt replaces the plan.
  const preparedPlanKey = JSON.stringify([initialRouteGeometry, initialInstructions, initialWaypoints]);
  const preparedPlanKeyRef = useRef(preparedPlanKey);
  const preparedPlanRef = useRef(null);
  preparedPlanRef.current = { initialRouteGeometry, initialInstructions, initialWaypoints };

  const watchIdRef = useRef(null);
  const browserStartPendingRef = useRef(false);
  const wakeLockRef = useRef(null);
  const wakeLockVisibilityHandlerRef = useRef(null);
  const screenAwakePluginRef = useRef(null);
  const activeRef = useRef(false);
  const mutedRef = useRef(false);
  const instructionsRef = useRef(instructions);
  const instructionIndexRef = useRef(0);
  const geometryRef = useRef(routeGeometry);
  const spokenRef = useRef(new Set());
  const speechUnlockedRef = useRef(false);
  const speechNeedsGestureRef = useRef(false);
  const offRouteFixesRef = useRef(0);
  const reroutingRef = useRef(false);
  // Stop/unmount invalidates route responses, browser startup and location/guidance events.
  const routeSessionRef = useRef(0);
  // A temporary destination belongs only to this navigation lifetime. Keep
  // its approved turnaround separately from the requested place: a partial
  // trip must turn back at the server-approved point, not the unreachable one.
  const destinationRouteRef = useRef(null);
  const lastRerouteAtRef = useRef(0);
  const initialRouteRequestedRef = useRef(false);
  const autoStartAttemptedRef = useRef(false);
  const nextChallengePositionRef = useRef(1);
  const onPositionRef = useRef(onPosition);
  const onChallengePassedRef = useRef(onChallengePassed);
  const onChallengeRepeatedRef = useRef(onChallengeRepeated);
  const waypointsRef = useRef(waypoints);
  const teachingChallengesRef = useRef(teachingChallenges);
  const deviceRef = useRef(device);
  const nativePluginRef = useRef(null);
  const nativeListenerHandlesRef = useRef([]);
  const nativeOperationRef = useRef(Promise.resolve());
  const nativeStartPendingRef = useRef(false);
  const nativeStoppingRef = useRef(false);
  const lessonEventIdRef = useRef(lessonEventId);
  const routeModeRef = useRef(normalizedRouteMode);
  const savedPart3ScopeRef = useRef(part3ScopeKey);
  const autoStartRef = useRef(autoStart);
  const autoStartDeferredRef = useRef(false);
  const autoStartCanceledLessonRef = useRef(null);
  const startRef = useRef(null);

  useEffect(() => { onPositionRef.current = onPosition; }, [onPosition]);
  useEffect(() => { onChallengePassedRef.current = onChallengePassed; }, [onChallengePassed]);
  useEffect(() => {
    onChallengeRepeatedRef.current = onChallengeRepeated;
  }, [onChallengeRepeated]);
  useEffect(() => { mutedRef.current = muted; }, [muted]);
  useEffect(() => { instructionsRef.current = instructions; }, [instructions]);
  useEffect(() => { geometryRef.current = routeGeometry; }, [routeGeometry]);
  useEffect(() => { waypointsRef.current = waypoints; }, [waypoints]);
  useEffect(() => { teachingChallengesRef.current = teachingChallenges; }, [teachingChallenges]);
  useEffect(() => { deviceRef.current = device; }, [device]);
  useEffect(() => { autoStartRef.current = autoStart; }, [autoStart]);
  useEffect(() => {
    if (activeRef.current) return;
    setRouteGeometry(initialRouteGeometry);
    setInstructions(Array.isArray(initialInstructions) ? initialInstructions : EMPTY_NAVIGATION_ITEMS);
    setWaypoints(Array.isArray(initialWaypoints) ? initialWaypoints : EMPTY_NAVIGATION_ITEMS);
    setNativeGuidance(null);
    setInstructionIndex(0);
    instructionIndexRef.current = 0;
    part3InstructionProgressRef.current = null;
  }, [initialRouteGeometry, initialInstructions, initialWaypoints]);

  const speak = useCallback((message, key, fromUserGesture = false) => {
    if (!message || mutedRef.current || spokenRef.current.has(key)) return;
    if (!("speechSynthesis" in window) || !("SpeechSynthesisUtterance" in window)) {
      setSpeechStatus("unavailable");
      return;
    }
    if (fromUserGesture) speechUnlockedRef.current = true;
    if (!speechUnlockedRef.current) {
      speechNeedsGestureRef.current = true;
      setSpeechStatus("tap");
      return;
    }
    spokenRef.current.add(key);
    const utterance = new window.SpeechSynthesisUtterance(message);
    utterance.lang = "en-GB";
    utterance.rate = 0.96;
    utterance.onstart = () => {
      speechNeedsGestureRef.current = false;
      setSpeechStatus("ready");
    };
    utterance.onerror = event => {
      spokenRef.current.delete(key);
      if (event?.error === "not-allowed" || event?.error === "service-not-allowed") {
        speechUnlockedRef.current = false;
        speechNeedsGestureRef.current = true;
        setSpeechStatus("tap");
      } else {
        setSpeechStatus("error");
      }
    };
    window.speechSynthesis.cancel();
    try {
      window.speechSynthesis.speak(utterance);
    } catch {
      spokenRef.current.delete(key);
      speechUnlockedRef.current = false;
      speechNeedsGestureRef.current = true;
      setSpeechStatus("tap");
    }
  }, []);

  const unlockSpokenDirections = useCallback(() => {
    if (mutedRef.current) return;
    const current = instructionsRef.current[instructionIndexRef.current];
    const message = current?.message || "Spoken navigation is on.";
    const distance = distanceToInstruction;
    const prompt = current?.message && Number.isFinite(distance) && distance > 0 && distance <= 250
      ? `In ${formatNavigationDistance(distance)}, ${message}` : message;
    speechNeedsGestureRef.current = false;
    const key = `gesture-${instructionIndexRef.current}`;
    spokenRef.current.delete(key);
    speak(prompt, key, true);
  }, [distanceToInstruction, speak]);

  const updateChallengeProgress = useCallback((fix) => {
    const progress = navigationProgressAtFix(
      fix, waypointsRef.current, teachingChallenges, nextChallengePositionRef.current,
    );
    for (const challenge of progress.completed) {
      const completedChallenge = {
        ...challenge,
        completedAt: new Date().toISOString(),
      };
      setLastCompletedChallenge(completedChallenge);
      onChallengePassedRef.current?.({
        challenge: completedChallenge,
        device: fix,
        passedAt: completedChallenge.completedAt,
      });
    }
    nextChallengePositionRef.current = progress.nextChallengePosition;
  }, [teachingChallenges]);

  const applyPart3Route = useCallback(async (data, extraGuard = () => true) => {
    if (!isPart3Route) return false;
    const scope = part3ScopeKey;
    const session = data?.session;
    const revision = session?.route_revision;
    if (data?.status !== "OK" || session?.launch_id !== part3SessionId
        || session?.phase !== part3Phase || session?.phase_version !== part3PhaseVersion
        || !Number.isInteger(revision) || revision < part3RevisionRef.current
        || (parseRouteGeometry(data.route_geometry).length < 2 && data?.at_destination !== true)) {
      throw new Error("The Part 3 route no longer matches this stage. Retry its current route.");
    }
    const routeSession = routeSessionRef.current;
    const current = () => part3ScopeRef.current === scope && routeSessionRef.current === routeSession
      && revision >= part3RevisionRef.current && extraGuard();
    if (!current()) return false;
    part3RevisionRef.current = revision;
    const nextWaypoints = Array.isArray(data.navigation_waypoints) ? data.navigation_waypoints : EMPTY_NAVIGATION_ITEMS;
    const atDestination = data.at_destination === true;
    if (nativePluginRef.current && activeRef.current && !atDestination) {
      const plugin = nativePluginRef.current;
      const guarded = guardNativeGoogleCommands(plugin, () => current() && !nativeStoppingRef.current, ["updateRoute"]);
      const update = queueNativeGoogleOperation(plugin, () => current() && activeRef.current
        ? updateNativeGoogleNavigation(guarded, { lessonEventId: routeTargetId, waypoints: nextWaypoints }) : undefined);
      nativeOperationRef.current = update.catch(() => {});
      await update;
    }
    if (!current()) return false;
    // Arrival evidence has no replacement itinerary. Keep the last road
    // geometry and native destination while GPS confirms the parked dwell;
    // an empty waypoint list must never be sent to the native SDK.
    part3AtDestinationRef.current = atDestination;
    if (!atDestination) {
      setRouteGeometry(data.route_geometry || null);
      geometryRef.current = data.route_geometry || null;
    }
    const nextInstructions = Array.isArray(data.navigation_instructions) ? data.navigation_instructions : EMPTY_NAVIGATION_ITEMS;
    setInstructions(nextInstructions); instructionsRef.current = nextInstructions;
    setWaypoints(nextWaypoints); waypointsRef.current = nextWaypoints;
    setDestinationPoint(data.destination_point || session.phase_destination || null);
    setInstructionIndex(0); instructionIndexRef.current = 0;
    part3InstructionProgressRef.current = null;
    setNativeGuidance(null); spokenRef.current.clear(); offRouteFixesRef.current = 0;
    setError(null); setStatus(data.at_destination ? "At your Part 3 destination" : "Part 3 guidance active");
    setRouteNotice(data.message || null);
    onPart3RouteRef.current?.(data);
    return true;
  }, [isPart3Route, part3ScopeKey, part3SessionId, part3Phase, part3PhaseVersion, routeTargetId]);

  const reroute = useCallback(async (fix, options = {}) => {
    if (!routeTargetId || reroutingRef.current) return false;
    const now = Date.now();
    if (!options.force && now - lastRerouteAtRef.current < 15000) return;
    const routeSession = routeSessionRef.current;
    const isCurrentSession = () => routeSession === routeSessionRef.current;
    const destinationContext = destinationRouteRef.current;
    if (destinationContext?.returnOnly && (options.destination || options.repeatChallengePosition != null)) return false;
    const isCurrentRoute = () => isCurrentSession() && destinationRouteRef.current === destinationContext;
    const requestedDestination = options.destination
      || (options.repeatChallengePosition == null && !destinationContext?.returnOnly ? destinationContext?.destination : null);
    const returnOnly = Boolean(destinationContext?.returnOnly);
    reroutingRef.current = true;
    lastRerouteAtRef.current = now;
    setRerouting(true);
    setStatus("Updating route…");
    try {
      if (isPart3Route) {
        // Part 3 owns the complete itinerary and clock. Never let the generic
        // readiness, teaching, Repeat, home or destination endpoints run here.
        if (!part3SessionId || !part3Phase || !Number.isInteger(part3PhaseVersion)) {
          throw new Error("The current Part 3 stage could not be confirmed.");
        }
        if (options.destination || options.repeatChallengePosition != null) {
          throw new Error("Use Part 3 voice control to change this specific circuit.");
        }
        const captured = Number(fix?.requestedAt);
        if (!Number.isFinite(captured) || captured > Date.now() + 5000 || Date.now() - captured > 15000
            || !Number.isFinite(fix?.accuracy) || fix.accuracy < 0 || fix.accuracy > 100) {
          throw new Error("A fresh, precise location is needed to update the Part 3 route.");
        }
        const scope = part3ScopeKey;
        const { data } = await axios.post(
          `${API}/api/work-mode/part3-sessions/${encodeURIComponent(part3SessionId)}/navigation-route`,
          { expected_phase: part3Phase, phase_version: part3PhaseVersion,
            location: { latitude: Number(fix.latitude), longitude: Number(fix.longitude),
              accuracy_metres: fix.accuracy, captured_at: new Date(captured).toISOString(),
              ...(Number.isFinite(fix.speed) && fix.speed >= 0 ? { speed: fix.speed } : {}) },
            recent_locations: part3RecentFixesRef.current
              .filter(row => Date.now() - row.requestedAt <= 60000 && row.requestedAt <= captured)
              .map(row => ({ latitude: row.latitude, longitude: row.longitude,
                accuracy_metres: row.accuracy, captured_at: new Date(row.requestedAt).toISOString(),
                ...(Number.isFinite(row.speed) ? { speed: row.speed } : {}) })) },
          { withCredentials: true, timeout: 25000 },
        );
        if (!isCurrentSession() || part3ScopeRef.current !== scope) return false;
        if (data?.status !== "OK") throw new Error(data?.message || "This Part 3 route is unavailable. Return safely; do not rush.");
        return await applyPart3Route(data, () => isCurrentSession() && part3ScopeRef.current === scope);
      }
      const payload = isHomeRoute
        ? {
          final_lesson_id: routeTargetId,
          current_location: {
            latitude: Number(fix.latitude),
            longitude: Number(fix.longitude),
            accuracy_metres: Number.isFinite(Number(fix.accuracy)) ? Number(fix.accuracy) : null,
            captured_at: new Date().toISOString(),
          },
        }
        : buildNavigationRoutePayload(
          fix,
          nextChallengePositionRef.current,
          options.repeatChallengePosition,
          requestedDestination,
          returnOnly,
        );
      // Pickup guidance has a different destination from the teaching plan.
      // Readiness already owns the authenticated current-location → pupil route.
      const response = isPickupRoute
        ? await axios.get(
          `${API}/api/run-my-day/lessons/${encodeURIComponent(routeTargetId)}/readiness`,
          {
            withCredentials: true, timeout: 20000,
            params: {
              device_latitude: Number(fix.latitude),
              device_longitude: Number(fix.longitude),
            },
          },
        )
        : await axios.post(
          isHomeRoute
            ? `${API}/api/run-my-day/home-route`
            : `${API}/api/run-my-day/lessons/${encodeURIComponent(routeTargetId)}/navigation-route`,
          payload,
          { withCredentials: true },
        );
      if (!isCurrentRoute()) return false;
      const data = response.data || {};
      const pickupDestination = data.destination_point;
      const hasPickupDestination = pickupDestination?.latitude != null
        && pickupDestination?.longitude != null
        && Number.isFinite(Number(pickupDestination.latitude))
        && Number.isFinite(Number(pickupDestination.longitude));
      if (isPickupRoute && data.status === "AT_PICKUP" && hasPickupDestination
          && distanceMeters(fix, pickupDestination) <= 35
          && parseRouteGeometry(data.route_geometry).length < 2) {
        // Google need not return a drive when already near pickup. Keep the
        // live fix for the stricter arrival dwell; never invent a route or arrive.
        setDestinationPoint(pickupDestination);
        setRouteGeometry(null);
        geometryRef.current = null;
        setInstructions(EMPTY_NAVIGATION_ITEMS);
        instructionsRef.current = EMPTY_NAVIGATION_ITEMS;
        setWaypoints(EMPTY_NAVIGATION_ITEMS);
        waypointsRef.current = EMPTY_NAVIGATION_ITEMS;
        setError(null);
        setStatus("Near pupil pickup");
        setRouteNotice("When safely parked at the pupil, open briefing.");
        return true;
      }
      const routeOkay = isPickupRoute
        ? data.route_status === "OK" && parseRouteGeometry(data.route_geometry).length >= 2
        : data.status === "OK" && parseRouteGeometry(data.route_geometry).length >= 2;
      if (!routeOkay) {
        if (isPickupRoute && data.status === "AT_PICKUP") {
          throw new Error("Google could not confirm a route to the pupil pickup. Retry when parked.");
        }
        throw new Error(data.message || data.reason || "Google could not update this route.");
      }
      const nextInstructions = response.data.navigation_instructions || [];
      const nextWaypoints = response.data.navigation_waypoints || [];
      const destinationTarget = requestedDestination ? nextWaypoints.find(point => point.kind !== "return"
        && Number.isFinite(point.latitude) && Number.isFinite(point.longitude)) : null;
      if (requestedDestination && !destinationTarget) {
        throw new Error("The approved destination waypoint is missing. Your current route remains active.");
      }
      if (nativePluginRef.current && activeRef.current) {
        const plugin = nativePluginRef.current;
        const guardedPlugin = guardNativeGoogleCommands(
          plugin,
          () => isCurrentRoute() && !nativeStoppingRef.current,
          ["updateRoute"],
        );
        const update = queueNativeGoogleOperation(plugin, () => {
          if (!isCurrentRoute() || !activeRef.current) return undefined;
          return updateNativeGoogleNavigation(guardedPlugin, {
            lessonEventId: routeTargetId,
            waypoints: nextWaypoints,
          });
        });
        // Stop must follow any SDK update already in flight. Keep the operation
        // queue usable on rejection while reporting the current route error below.
        nativeOperationRef.current = update.catch(() => {});
        await update;
      }
      if (!isCurrentRoute()) return false;
      // Publish only after native accepts; failed updates retain the current route.
      if (requestedDestination) {
        destinationRouteRef.current = {
          destination: { ...requestedDestination },
          target: { latitude: destinationTarget.latitude, longitude: destinationTarget.longitude },
          returnOnly: false,
        };
      } else if (options.repeatChallengePosition != null) {
        destinationRouteRef.current = null;
      }
      if (isPickupRoute && hasPickupDestination) setDestinationPoint(pickupDestination);
      setError(null);
      setRouteGeometry(response.data.route_geometry);
      geometryRef.current = response.data.route_geometry;
      setInstructions(nextInstructions);
      instructionsRef.current = nextInstructions;
      setWaypoints(nextWaypoints);
      waypointsRef.current = nextWaypoints;
      instructionIndexRef.current = 0;
      setInstructionIndex(0);
      setNativeGuidance(null);
      spokenRef.current.clear();
      offRouteFixesRef.current = 0;
      setStatus(response.data?.diversion_status === "PARTIAL"
        ? "Turn back at the marked point"
        : "Route updated");
      setRouteNotice(response.data?.message || null);
      const repeated = teachingChallengeForStop(
        response.data?.repeat_challenge, teachingChallengesRef.current,
      );
      if (repeated) {
        setLastCompletedChallenge({
          ...repeated,
          completedAt: new Date().toISOString(),
        });
        onChallengeRepeatedRef.current?.({
          challenge: repeated,
          device: fix,
          requestedAt: now,
        });
        speak(
          `Returning to ${repeated.category_label || repeated.title || "the last challenge"}.`,
          `repeat-${now}`,
        );
      } else if (response.data?.diversion_status === "PARTIAL") {
        speak(
          response.data?.message || "The route ends at the approved turnaround point.",
          `diversion-partial-${now}`,
        );
      } else {
        speak("Route updated.", `reroute-${now}`);
      }
      return true;
    } catch (requestError) {
      if (!isCurrentRoute()) return false;
      offRouteFixesRef.current = 0;
      setStatus("Stay safe — route update unavailable");
      setError(
        (isPart3Route ? requestError?.response?.data?.detail?.message : requestError?.response?.data?.detail)
        || requestError?.message
        || "Google could not update this route.",
      );
      return false;
    } finally {
      if (isCurrentSession()) {
        reroutingRef.current = false;
        setRerouting(false);
        // Arrival can invalidate an outbound response while HTTP or the SDK
        // is finishing. Reassert the direct return after that request settles.
        if (destinationContext && !destinationContext.returnOnly
            && destinationRouteRef.current?.returnOnly && activeRef.current) {
          void reroute(deviceRef.current || fix, { force: true });
        }
      }
    }
  }, [isHomeRoute, isPickupRoute, isPart3Route, part3SessionId, part3Phase, part3PhaseVersion, part3ScopeKey, applyPart3Route, routeTargetId, speak]);

  const repeatLastChallenge = useCallback(() => {
    const position = Number(lastCompletedChallenge?.position);
    if (isPart3Route || !activeRef.current || destinationRouteRef.current?.returnOnly || !Number.isFinite(position)) return false;
    const fix = device;
    if (!Number.isFinite(Number(fix?.latitude)) || !Number.isFinite(Number(fix?.longitude))) return false;
    return reroute(fix, { repeatChallengePosition: position });
  }, [device, isPart3Route, lastCompletedChallenge, reroute]);

  const goToDestination = useCallback(async (destination) => {
    const fix = deviceRef.current;
    if (isPart3Route) {
      setError("Use Part 3 voice control to change this specific circuit.");
      return false;
    }
    if (destinationRouteRef.current?.returnOnly) {
      setError("The route is returning to the pupil. A new destination cannot replace that return.");
      return false;
    }
    if (isHomeRoute || isPickupRoute || !routeTargetId || !activeRef.current) {
      setError("Start live lesson navigation before choosing a stop.");
      return false;
    }
    if (!Number.isFinite(Number(fix?.latitude)) || !Number.isFinite(Number(fix?.longitude))) {
      setError("A current location is needed before changing the route.");
      return false;
    }
    setError(null);
    setRouteNotice(null);
    return reroute(fix, { destination, force: true });
  }, [isHomeRoute, isPickupRoute, isPart3Route, reroute, routeTargetId]);

  const processFix = useCallback((fix, { native = false } = {}) => {
    if (isPart3Route) {
      const now = Date.now();
      if (!freshPart3NavigationFix(fix, now)
          || part3LastFixAtRef.current != null && fix.requestedAt <= part3LastFixAtRef.current) return;
      part3LastFixAtRef.current = fix.requestedAt;
      part3RecentFixesRef.current = part3RecentFixesAtFix(part3RecentFixesRef.current, fix, now);
    }
    deviceRef.current = fix;
    onPositionRef.current?.(fix);
    if (isPart3Route && part3Phase === "circuit") {
      const now = Date.now();
      const key = part3PendingStopAtFix(fix, waypointsRef.current, now);
      const previous = part3StopProbeRef.current;
      if (key && !reroutingRef.current && (previous?.key !== key
          || (!previous.confirmed && now - previous.attemptedAt >= 15000))) {
        const probe = { key, attemptedAt: now, confirmed: false };
        part3StopProbeRef.current = probe;
        // Observe the authored point while its fresh fix is still available.
        // The 30-second refresh remains a clock/provider fallback, not the
        // only opportunity to observe a short visit to a route point.
        void reroute(fix, { force: true }).then(applied => {
          if (part3StopProbeRef.current === probe) probe.confirmed = Boolean(applied);
        });
      }
    }
    if (isPart3Route && part3AtDestinationRef.current) {
      setStatus("At your Part 3 destination");
      return;
    }
    // A fix can bootstrap pickup or a saved lesson recipe. GPS alone must not
    // report guidance or teaching encounters before its road route arrives.
    if (!native && parseRouteGeometry(geometryRef.current).length < 2) return;
    const destinationContext = destinationRouteRef.current;
    if (destinationContext) {
      // A searched destination is navigation, never evidence of a teaching
      // challenge. Once its actual approved stop is reached, every later
      // reroute requests only the server-owned return point.
      if (!destinationContext.returnOnly && distanceMeters(fix, destinationContext.target) <= 65) {
        destinationRouteRef.current = { ...destinationContext, returnOnly: true };
        if (!reroutingRef.current) void reroute(fix, { force: true });
      }
    } else if (!isPickupRoute && !isPart3Route) updateChallengeProgress(fix);

    if (native) {
      if (!reroutingRef.current) setStatus("On route");
      return;
    }

    const currentInstructions = instructionsRef.current || [];
    let index = Math.min(
      instructionIndexRef.current,
      Math.max(0, currentInstructions.length - 1),
    );
    if (isPart3Route) {
      const progress = part3InstructionProgressAtFix(fix, currentInstructions, part3InstructionProgressRef.current);
      part3InstructionProgressRef.current = progress;
      index = progress.index;
    } else {
      while (
        index < currentInstructions.length - 1
        && distanceMeters(fix, currentInstructions[index]) <= 30
      ) {
        index += 1;
      }
    }
    if (index !== instructionIndexRef.current) {
      instructionIndexRef.current = index;
      setInstructionIndex(index);
    }
    const next = currentInstructions[index];
    const distance = next ? distanceMeters(fix, next) : null;
    setDistanceToInstruction(Number.isFinite(distance) ? distance : null);
    if (next && Number.isFinite(distance)) {
      const message = next.message || "Continue";
      if (distance <= 60) {
        speak(message, `${index}-now`);
      } else if (distance <= 250) {
        speak(`In ${formatNavigationDistance(distance)}, ${message}`, `${index}-soon`);
      }
    }

    const offRouteDistance = nearestRouteDistanceMeters(fix, geometryRef.current);
    if (Number.isFinite(offRouteDistance) && offRouteDistance > 90) {
      offRouteFixesRef.current += 1;
      if (offRouteFixesRef.current >= 3) reroute(fix);
    } else {
      offRouteFixesRef.current = 0;
      if (!reroutingRef.current) setStatus("On route");
    }
  }, [isPickupRoute, isPart3Route, part3Phase, reroute, speak, updateChallengeProgress]);

  const clearNativeListeners = useCallback(() => {
    const handles = nativeListenerHandlesRef.current.splice(0);
    handles.forEach((handle) => {
      removeNativeGoogleListener(handle).catch(() => {});
    });
  }, []);

  const stop = useCallback((options = {}) => {
    const { preserveDeferredAutoStart = false } = options;
    if (!preserveDeferredAutoStart) {
      autoStartDeferredRef.current = false;
      autoStartCanceledLessonRef.current = lessonEventIdRef.current;
    }
    if (nativeStoppingRef.current) {
      return nativeOperationRef.current;
    }
    routeSessionRef.current += 1;
    part3LastFixAtRef.current = null;
    part3RecentFixesRef.current = [];
    part3InstructionProgressRef.current = null;
    if (destinationRouteRef.current) {
      destinationRouteRef.current = null;
      const prepared = preparedPlanRef.current;
      const nextInstructions = Array.isArray(prepared.initialInstructions) ? prepared.initialInstructions : EMPTY_NAVIGATION_ITEMS;
      const nextWaypoints = Array.isArray(prepared.initialWaypoints) ? prepared.initialWaypoints : EMPTY_NAVIGATION_ITEMS;
      setRouteGeometry(prepared.initialRouteGeometry);
      geometryRef.current = prepared.initialRouteGeometry;
      setInstructions(nextInstructions);
      instructionsRef.current = nextInstructions;
      setWaypoints(nextWaypoints);
      waypointsRef.current = nextWaypoints;
    }
    nativeStartPendingRef.current = false;
    browserStartPendingRef.current = false;
    reroutingRef.current = false;
    lastRerouteAtRef.current = 0;
    setRerouting(false);
    if (watchIdRef.current != null && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
    watchIdRef.current = null;
    initialRouteRequestedRef.current = false;
    activeRef.current = false;
    setActive(false);
    setRequesting(false);
    setStatus("Paused");
    setNativeGuidance(null);
    if (nativePluginRef.current) {
      const plugin = nativePluginRef.current;
      nativeStoppingRef.current = true;
      setError(null);
      setRequesting(true);
      setStatus("Stopping Google…");
      // A late start must settle before stop, and another start must not overtake it.
      nativeOperationRef.current = queueNativeGoogleOperation(plugin, async () => {
        try {
          if (typeof plugin.stop !== "function") throw new Error("Google stop is unavailable");
          await stopNativeGoogleNavigation(plugin);
          delete plugin[NATIVE_TOMTOM_STOP_FAILURE];
        } catch (stopError) {
          // This SDK is shared by remounted hook instances. A rejected Stop
          // leaves ownership uncertain for ALL of them, not only this hook.
          plugin[NATIVE_TOMTOM_STOP_FAILURE] = stopError || new Error("Google stop not confirmed");
          throw plugin[NATIVE_TOMTOM_STOP_FAILURE];
        }
      })
        .then(() => {
          nativePluginRef.current = null;
          nativeStoppingRef.current = false;
          setRequesting(false);
          setStatus("Paused");
          if (
            autoStartDeferredRef.current
            && autoStartRef.current
            && lessonEventIdRef.current
            && autoStartCanceledLessonRef.current !== lessonEventIdRef.current
            && (routeModeRef.current === "pickup" || parseRouteGeometry(geometryRef.current).length >= 2
              || (routeModeRef.current === "lesson" && hasPreparedLessonItinerary(waypointsRef.current)))
          ) {
            autoStartDeferredRef.current = false;
            autoStartAttemptedRef.current = true;
            startRef.current();
          }
          return true;
        })
        .catch((stopError) => {
          // Retain the plugin so the next attempt retries Stop instead of starting again.
          nativeStoppingRef.current = false;
          setRequesting(false);
          setStatus("Google stop not confirmed");
          setError(stopError?.message || "Google could not confirm it stopped. Try Stop again.");
          return false;
        });
    }
    clearNativeListeners();
    setNativeMode(false);
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
    if (wakeLockVisibilityHandlerRef.current && typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", wakeLockVisibilityHandlerRef.current);
      wakeLockVisibilityHandlerRef.current = null;
    }
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {});
      wakeLockRef.current = null;
    }
    if (screenAwakePluginRef.current) {
      const plugin = screenAwakePluginRef.current;
      screenAwakePluginRef.current = null;
      setNativeScreenAwake(plugin, false).catch(() => {});
    }
    setScreenAwake("off");
    // Boolean receipt: callers may finish a journey only after actual shutdown.
    return nativeStoppingRef.current ? nativeOperationRef.current : Promise.resolve(true);
  }, [clearNativeListeners]);

  useLayoutEffect(() => {
    const sameScope = lessonEventIdRef.current === lessonEventId && routeModeRef.current === normalizedRouteMode
      && savedPart3ScopeRef.current === part3ScopeKey;
    if (sameScope && preparedPlanKeyRef.current === preparedPlanKey) return;
    const cancelledLesson = autoStartCanceledLessonRef.current;
    lessonEventIdRef.current = lessonEventId;
    routeModeRef.current = normalizedRouteMode;
    savedPart3ScopeRef.current = part3ScopeKey;
    if (!sameScope) {
      part3RevisionRef.current = -1;
      part3StopProbeRef.current = null;
      part3AtDestinationRef.current = false;
      part3LastFixAtRef.current = null;
      part3RecentFixesRef.current = [];
      part3InstructionProgressRef.current = null;
    }
    preparedPlanKeyRef.current = preparedPlanKey;
    stop({ preserveDeferredAutoStart: true });
    autoStartCanceledLessonRef.current = sameScope ? cancelledLesson : null;
    initialRouteRequestedRef.current = false;
    autoStartAttemptedRef.current = false;
    nextChallengePositionRef.current = 1;
    offRouteFixesRef.current = 0;
    spokenRef.current.clear();
    setLastCompletedChallenge(null);
    setDestinationPoint(null);
    setRouteNotice(null);
    setError(null);
    setRouteGeometry(initialRouteGeometry);
    geometryRef.current = initialRouteGeometry;
    const phaseInstructions = Array.isArray(initialInstructions) ? initialInstructions : EMPTY_NAVIGATION_ITEMS;
    const phaseWaypoints = Array.isArray(initialWaypoints) ? initialWaypoints : EMPTY_NAVIGATION_ITEMS;
    setInstructions(phaseInstructions);
    instructionsRef.current = phaseInstructions;
    setWaypoints(phaseWaypoints);
    waypointsRef.current = phaseWaypoints;
    setNativeGuidance(null);
    setDistanceToInstruction(null);
    setInstructionIndex(0);
    instructionIndexRef.current = 0;
  }, [lessonEventId, normalizedRouteMode, part3ScopeKey, preparedPlanKey, initialRouteGeometry, initialInstructions, initialWaypoints, stop]);

  const start = useCallback(async () => {
    if (nativeStoppingRef.current) return;
    if (
      activeRef.current || browserStartPendingRef.current || watchIdRef.current != null
      || nativeStartPendingRef.current || nativePluginRef.current
    ) {
      stop();
      return;
    }
    setError(null);

    // Part 3 needs continuous pass-through guidance at its roundabouts. The
    // current mobile bridge has no automatic waypoint-advance contract, so
    // this web release follows the server-proved Google polyline/instructions
    // through the browser path even when a native plugin is present.
    // Ordinary pickup also retains its existing browser-guidance behavior.
    const nativePlugin = isPickupRoute || isPart3Route ? null : getNativeGoogleNavigationPlugin();
    if (nativePlugin && routeTargetId && waypointsRef.current.length > 0) {
      const nativeSession = ++routeSessionRef.current;
      const isCurrentSession = () => nativeSession === routeSessionRef.current;
      setRequesting(true);
      setStatus("Starting Google…");
      nativePluginRef.current = nativePlugin;
      nativeStartPendingRef.current = true;
      clearNativeListeners();
      const startup = queueNativeGoogleOperation(nativePlugin, async (priorStopFailure) => {
        if (!isCurrentSession()) return;
        const guardedPlugin = guardNativeGoogleCommands(
          nativePlugin,
          () => isCurrentSession() && !nativeStoppingRef.current,
          ["start"],
        );
        const registerListener = async (name, callback) => {
          const handle = await addNativeGoogleListener(nativePlugin, name, callback);
          if (!isCurrentSession()) {
            await removeNativeGoogleListener(handle);
            return false;
          }
          // Track each handle immediately so a partial registration can be cleaned up.
          if (handle) nativeListenerHandlesRef.current.push(handle);
          return true;
        };
        try {
          // Recover the uncertain shutdown through the existing error/Stop
          // path. Never issue a replacement Start merely because the queue's
          // rejected promise was caught to keep later cleanup usable.
          if (priorStopFailure) throw priorStopFailure;
          if (!await registerListener("location", (event) => {
            if (!isCurrentSession()) return;
            const fix = {
              status: "active",
              latitude: Number(event?.latitude),
              longitude: Number(event?.longitude),
              accuracy: event?.accuracy == null ? null : Number(event.accuracy),
              heading: event?.heading == null ? null : Number(event.heading),
              speed: event?.speed == null ? null : Number(event.speed),
              requestedAt: Date.now(),
            };
            if (!Number.isFinite(fix.latitude) || !Number.isFinite(fix.longitude)) return;
            activeRef.current = true;
            setActive(true);
            setRequesting(false);
            setDevice(fix);
            processFix(fix, { native: true });
          })) return;
          if (!await registerListener("guidance", (event) => {
            if (!isCurrentSession()) return;
            if (isPart3Route && part3AtDestinationRef.current) return;
            setNativeGuidance(event?.message ? { message: String(event.message) } : null);
            setDistanceToInstruction(
              Number.isFinite(Number(event?.distanceMeters))
                ? Number(event.distanceMeters)
                : null,
            );
            if (event?.status) setStatus(String(event.status));
          })) return;
          if (!await registerListener("rerouteRequired", (event) => {
            if (!isCurrentSession()) return;
            const eventFix = {
              latitude: Number(event?.latitude),
              longitude: Number(event?.longitude),
            };
            const latest = isPart3Route ? deviceRef.current : (
              Number.isFinite(eventFix.latitude) && Number.isFinite(eventFix.longitude)
                ? eventFix
                : deviceRef.current
            );
            if (
              Number.isFinite(Number(latest?.latitude))
              && Number.isFinite(Number(latest?.longitude))
            ) {
              reroute(latest);
            }
          })) return;
          await startNativeGoogleNavigation(guardedPlugin, {
            lessonEventId: routeTargetId,
            waypoints: waypointsRef.current,
            muted: mutedRef.current,
          });
          if (!isCurrentSession()) return;
          activeRef.current = true;
          setActive(true);
          setRequesting(false);
          setNativeMode(true);
          setStatus("On route");
          return;
        } catch (nativeError) {
          if (!isCurrentSession()) return;
          // Startup can fail after side effects; invalidate callbacks and confirm shutdown.
          stop();
          setError(nativeError?.message || "Native Google navigation could not start.");
        } finally {
          nativeStartPendingRef.current = false;
        }
      });
      nativeOperationRef.current = startup;
      return startup;
    }

    const savedLessonItinerary = normalizedRouteMode === "lesson"
      && hasPreparedLessonItinerary(waypointsRef.current);
    if (!routeTargetId || (!isPart3Route && !isPickupRoute && !savedLessonItinerary
        && parseRouteGeometry(geometryRef.current).length < 2)) {
      setError("A prepared Google route is required first.");
      return;
    }
    if (!("geolocation" in navigator)) {
      setDevice((previous) => ({ ...previous, status: "unavailable" }));
      setError("Location is unavailable on this device.");
      return;
    }
    setRequesting(true);
    setStatus("Locating…");
    const browserSession = ++routeSessionRef.current;
    const isCurrentSession = () => browserSession === routeSessionRef.current;
    browserStartPendingRef.current = true;
    try {
      const screenPlugin = getNativeGoogleNavigationPlugin();
      if (typeof screenPlugin?.setScreenAwake === "function") {
        if (await setNativeScreenAwake(screenPlugin, true)) {
          if (!isCurrentSession()) {
            await setNativeScreenAwake(screenPlugin, false);
            return;
          }
          screenAwakePluginRef.current = screenPlugin;
          setScreenAwake("on");
        }
      }
      if (!screenAwakePluginRef.current && navigator.wakeLock?.request) {
        const acquireBrowserWakeLock = async () => {
          if (!isCurrentSession() || document.visibilityState === "hidden") return false;
          const wakeLock = await navigator.wakeLock.request("screen");
          if (!isCurrentSession()) {
            wakeLock.release().catch(() => {});
            return false;
          }
          wakeLockRef.current = wakeLock;
          wakeLock.addEventListener?.("release", () => {
            if (isCurrentSession() && activeRef.current && document.visibilityState !== "hidden") {
              setScreenAwake("released");
            }
          });
          setScreenAwake("on");
          return true;
        };
        await acquireBrowserWakeLock();
        const onVisibilityChange = () => {
          if (document.visibilityState === "visible" && isCurrentSession() && activeRef.current) {
            acquireBrowserWakeLock().catch(() => setScreenAwake("failed"));
          }
        };
        wakeLockVisibilityHandlerRef.current = onVisibilityChange;
        document.addEventListener("visibilitychange", onVisibilityChange);
      } else if (!screenAwakePluginRef.current) {
        setScreenAwake("unsupported");
      }
    } catch {
      if (!isCurrentSession()) return;
      wakeLockRef.current = null;
      setScreenAwake("failed");
    }
    if (!isCurrentSession()) return;
    browserStartPendingRef.current = false;
    watchIdRef.current = navigator.geolocation.watchPosition(
      (position) => {
        if (!isCurrentSession()) return;
        const fix = {
          status: "active",
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          heading: position.coords.heading,
          speed: position.coords.speed,
          requestedAt: isPart3Route ? (Number.isFinite(position.timestamp) ? position.timestamp : null) : Date.now(),
        };
        if (isPart3Route && (!freshPart3NavigationFix(fix)
            || part3LastFixAtRef.current != null && fix.requestedAt <= part3LastFixAtRef.current)) return;
        activeRef.current = true;
        setActive(true);
        setRequesting(false);
        setDevice(fix);
        deviceRef.current = fix;
        if (!initialRouteRequestedRef.current) {
          initialRouteRequestedRef.current = true;
          reroute(fix);
        }
        processFix(fix);
      },
      (positionError) => {
        if (!isCurrentSession()) return;
        const denied = positionError.code === positionError.PERMISSION_DENIED;
        activeRef.current = false;
        setActive(false);
        setRequesting(false);
        setDevice((previous) => ({
          ...previous,
          status: denied ? "denied" : "unavailable",
        }));
        setError(denied ? "Location permission is needed." : "Current location is unavailable.");
        setStatus(denied ? "Location permission needed" : "Location unavailable");
      },
      { enableHighAccuracy: true, maximumAge: 3000, timeout: 15000 },
    );
  }, [clearNativeListeners, isPickupRoute, isPart3Route, normalizedRouteMode, processFix, reroute, routeTargetId, stop]);

  const retryRoute = useCallback(async () => {
    const fix = deviceRef.current;
    if (activeRef.current && fix?.latitude != null && fix?.longitude != null
        && Number.isFinite(Number(fix.latitude)) && Number.isFinite(Number(fix.longitude))) {
      return reroute(fix, { force: true });
    }
    // An errored GPS watch may still exist. Retry explicitly replaces it once;
    // permission denial never schedules an automatic permission loop.
    if (watchIdRef.current != null || nativePluginRef.current) {
      if (!await stop()) return false;
    }
    return start();
  }, [reroute, start, stop]);

  useEffect(() => { startRef.current = start; }, [start]);

  useEffect(() => {
    if (!isPart3Route || !active) return undefined;
    const timer = setInterval(() => {
      const fix = deviceRef.current;
      if (!activeRef.current || !Number.isFinite(fix?.requestedAt)
          || Date.now() - fix.requestedAt > 15000 || fix.requestedAt > Date.now() + 5000) return;
      void reroute(fix, { force: true });
    }, 30000);
    return () => clearInterval(timer);
  }, [isPart3Route, active, part3ScopeKey, reroute]);

  useEffect(() => {
    if (!nativePluginRef.current || !activeRef.current) return;
    const plugin = nativePluginRef.current;
    const routeSession = routeSessionRef.current;
    const guardedPlugin = guardNativeGoogleCommands(
      plugin,
      () => routeSession === routeSessionRef.current && !nativeStoppingRef.current,
      ["setMuted"],
    );
    setNativeGoogleMuted(guardedPlugin, muted).catch(() => {});
  }, [muted]);

  useEffect(() => {
    if (!autoStart || autoStartAttemptedRef.current || !routeTargetId) return;
    if (!isPart3Route && !isPickupRoute && parseRouteGeometry(routeGeometry).length < 2
        && !(normalizedRouteMode === "lesson" && hasPreparedLessonItinerary(waypoints))) return;
    if (autoStartCanceledLessonRef.current === routeTargetId) return;
    if (nativeStoppingRef.current) {
      autoStartDeferredRef.current = true;
      return;
    }
    autoStartAttemptedRef.current = true;
    autoStartDeferredRef.current = false;
    autoStartCanceledLessonRef.current = null;
    start();
  }, [autoStart, isPickupRoute, isPart3Route, normalizedRouteMode, routeGeometry, waypoints, routeTargetId, start]);

  useEffect(() => () => stop(), [stop]);

  const currentInstruction = useMemo(
    () => nativeGuidance || instructions[instructionIndex] || null,
    [nativeGuidance, instructions, instructionIndex],
  );

  return {
    active,
    requesting,
    muted,
    setMuted,
    speechStatus,
    unlockSpokenDirections,
    device,
    routeGeometry,
    instructions,
    currentInstruction,
    distanceToInstruction,
    status,
    error,
    routeNotice,
    destinationPoint,
    rerouting,
    lastCompletedChallenge,
    nativeMode,
    screenAwake,
    waypoints,
    canRepeat: !isPart3Route && active && Boolean(lastCompletedChallenge) && !rerouting && !destinationRouteRef.current?.returnOnly,
    repeatLastChallenge,
    goToDestination,
    retryRoute,
    applyPart3Route: isPart3Route ? applyPart3Route : null,
    toggle: start,
    stop,
  };
}


